import {
  ORBI_REQUEST_CONTEXT_MAP,
  ORBI_RESULTS_DETAIL_URL_FRAGMENT,
  ORBI_RESULTS_TREND_URL_FRAGMENT,
} from '@patient-ui/shared/constants';
import { OrbiFeedbackType } from '@patient-ui/shared/enums';
import { Report } from '@patient-ui/shared/models';

/**
 * Orbi session + request-context helpers (PCSA-13164 / PCSA-13222). Pure
 * functions; no Angular / framework dependency.
 */

/**
 * Generates a fresh conversation id (RFC 4122 v4). Prefers the native Web
 * Crypto `randomUUID` (all supported browsers); falls back to a
 * `getRandomValues`/`Math.random`-built v4 string when `randomUUID` is
 * unavailable (e.g. some test/SSR environments) so the client ALWAYS supplies a
 * non-empty `sessionId` (the gateway 400s on empty). No `uuid` dependency is
 * added. Mirrors mobile's `const Uuid().v4()`.
 */
export function generateOrbiSessionId(): string {
  const cryptoObj: Crypto | undefined =
    typeof crypto !== 'undefined' ? crypto : undefined;

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }

  // Fallback: build an RFC 4122 v4 string from random bytes.
  const bytes = new Uint8Array(16);
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // Version (4) and variant (10xx) bits per RFC 4122 (intentional bit ops).
  /* eslint-disable no-bitwise */
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  /* eslint-enable no-bitwise */
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-` +
    `${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-` +
    `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
  );
}

/**
 * sessionStorage key holding the "AI Agent locked for this login session" flag
 * (PCSA-13170). sessionStorage (not localStorage) is used deliberately: it
 * survives a browser REFRESH within the same tab/session — so a locking
 * guardrail cannot be bypassed by refreshing — but is cleared when the tab/
 * session ends, matching "for the remainder of the current login session". It
 * is also cleared explicitly on logout.
 */
export const ORBI_SESSION_LOCK_KEY = 'orbiChatLockedForSession';

/** Safe sessionStorage access — no-throw if storage is unavailable (SSR/tests). */
function safeSessionStorage(): Storage | undefined {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Reads the persisted session-lock flag (PCSA-13170). Defaults to false. */
export function readOrbiSessionLock(): boolean {
  return safeSessionStorage()?.getItem(ORBI_SESSION_LOCK_KEY) === 'true';
}

/** Persists (or clears) the session-lock flag (PCSA-13170). */
export function writeOrbiSessionLock(locked: boolean): void {
  const store = safeSessionStorage();
  if (!store) {
    return;
  }
  if (locked) {
    store.setItem(ORBI_SESSION_LOCK_KEY, 'true');
  } else {
    store.removeItem(ORBI_SESSION_LOCK_KEY);
  }
}

/**
 * sessionStorage key holding the submitted response feedback, `messageId ->
 * sentiment` (PCSA-13171 / PCSA-13312).
 *
 * A rated response must stay rated: its thumbs latch and stop being actionable
 * once feedback is in. That latch lived only in the in-memory store, so a
 * browser REFRESH wiped it — and because conversation history is fetched from
 * the backend, re-opening a past conversation after a refresh brought back
 * responses the patient had already rated with both thumbs live again, letting
 * the same response be rated twice.
 *
 * sessionStorage for the same reason the lock above uses it: it survives a
 * refresh within the tab but ends with the login session, and it is cleared
 * explicitly on logout. Feedback the BACKEND already holds is the durable
 * record; this only keeps the UI honest for the rest of the session.
 */
export const ORBI_FEEDBACK_KEY = 'orbiFeedbackByMessageId';

/**
 * Reads the persisted `messageId -> sentiment` map. Defaults to `{}`, and drops
 * any entry that is not a known sentiment: the payload is user-writable, and a
 * stale or hand-edited value must not reach the store (an unrecognised sentiment
 * would latch a row into a state neither thumb can represent).
 */
export function readOrbiFeedback(): Record<string, OrbiFeedbackType> {
  const raw = safeSessionStorage()?.getItem(ORBI_FEEDBACK_KEY);
  if (!raw) {
    return {};
  }
  const sentiments: string[] = Object.values(OrbiFeedbackType);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.entries(parsed as Record<string, unknown>).reduce(
      (map, [messageId, sentiment]) => {
        if (typeof sentiment === 'string' && sentiments.includes(sentiment)) {
          map[messageId] = sentiment as OrbiFeedbackType;
        }
        return map;
      },
      {} as Record<string, OrbiFeedbackType>
    );
  } catch {
    return {};
  }
}

/**
 * Persists (or clears) the whole `messageId -> sentiment` map. An empty map
 * removes the key rather than storing `{}`, so logout leaves nothing behind.
 */
export function writeOrbiFeedback(
  feedbackByMessageId: Record<string, OrbiFeedbackType>
): void {
  const store = safeSessionStorage();
  if (!store) {
    return;
  }
  if (Object.keys(feedbackByMessageId).length === 0) {
    store.removeItem(ORBI_FEEDBACK_KEY);
    return;
  }
  try {
    store.setItem(ORBI_FEEDBACK_KEY, JSON.stringify(feedbackByMessageId));
  } catch {
    // Storage full / disabled mid-session: the in-memory latch still holds for
    // this page life, so a failed write must not break the submission.
  }
}

/**
 * Adds ONE rating to the persisted map, read-modify-write.
 *
 * Takes the incremental entry rather than the whole slice so the effect that
 * mirrors it does not have to re-read store state — and so the write cannot
 * clobber a rating submitted concurrently on another response (feedback effects
 * run with `mergeMap`, so two can be in flight at once).
 */
export function recordOrbiFeedback(
  messageId: string,
  feedbackType: OrbiFeedbackType
): void {
  writeOrbiFeedback({ ...readOrbiFeedback(), [messageId]: feedbackType });
}

/** Match on the path only, so a query value can't make a page look like another. */
function splitOrbiUrl(url: string | null | undefined): {
  path: string;
  query: string;
} {
  const withoutHash = (url ?? '').split('#')[0];
  const queryStart = withoutHash.indexOf('?');
  return queryStart === -1
    ? { path: withoutHash.toLowerCase(), query: '' }
    : {
        path: withoutHash.slice(0, queryStart).toLowerCase(),
        query: withoutHash.slice(queryStart + 1),
      };
}

/**
 * Derives the `requestContext` labels for the current router URL, matching
 * approved pages only (`ORBI_REQUEST_CONTEXT_MAP`). Returns `[]` when no
 * approved page matches — there is intentionally no `unknown -> [routeName]`
 * fallback (per the PCSA-13164 acceptance criteria). Matching is a
 * case-insensitive substring test on the URL path so trailing segments do not
 * defeat it.
 */
export function matchOrbiRequestContext(
  url: string | null | undefined
): string[] {
  const { path } = splitOrbiUrl(url);
  if (!path) {
    return [];
  }
  const match = ORBI_REQUEST_CONTEXT_MAP.find((entry) =>
    path.includes(entry.urlFragment)
  );
  return match ? [...match.context] : [];
}

/** `URLSearchParams` decodes `%XX` and `+` and never throws on a malformed escape. */
function serializeTrendContext(query: string): string | null {
  const panelName = new URLSearchParams(query).get('testName')?.trim();
  return panelName ? JSON.stringify({ panelName }) : null;
}

/**
 * A snapshot of the page data the portal has ALREADY loaded, read live at send
 * time and passed to `buildOrbiRequestContext`. No new data retrieval happens
 * here (PCSA-13222 AC 2) — these are the same store values the current page
 * rendered from. Every field is optional so a caller supplies only what applies
 * to the page the user is on; an empty/omitted snapshot yields nav labels only.
 */
export interface OrbiPageDataSnapshot {
  /** The loaded accession detail on the results DETAIL page (store: resultDetail). */
  resultDetail?: Report | null;
}

/**
 * Serializes the loaded results detail into the compact shape mobile sends as
 * its detail-context element (`AccessionDetail.toJson()` → `{ header,
 * orderedItems }`). Mirrors the mobile payload's fields so the agent receives
 * the SAME context web/mobile: the header descriptors (patient, provider,
 * dates, category, ordered test codes) plus the ordered items already loaded to
 * render the page. Values are the data on the user's screen — including PHI —
 * matching the shipped mobile→agent contract; nothing new is fetched.
 */
function serializeResultDetailContext(detail: Report): string | null {
  const header = detail.header;
  const payload = {
    header: header
      ? {
          id: header.id,
          patientName: header.patientName,
          orderingProviderName: header.orderingProviderName,
          accountName: header.accountName,
          dateOfService: header.dateOfService,
          orderedDate: header.orderedDate,
          reportDate: header.reportDate,
          dashboardCategory: header.dashboardCategory,
          pid: header.pid,
          testCodes: header.testCodes,
        }
      : undefined,
    orderedItems: detail.orderedItems,
  };
  try {
    return JSON.stringify(payload);
  } catch {
    // Defensive: a non-serializable payload (circular ref) must never break the
    // send — fall through to nav-label-only context.
    return null;
  }
}

/**
 * Builds the full `requestContext` for a message (PCSA-13222): the navigation
 * label(s) for the current page PLUS, when the portal has already loaded data
 * for that page, a detail element carrying that loaded data. Mirrors mobile's
 * two-layer `ScreenContextService` (`[...navigationContext, ...detailContext]`).
 *
 * Design notes (parity + the story's ACs):
 *  - Nav layer: `matchOrbiRequestContext(url)` — approved-page labels only,
 *    `[]` otherwise (AC 3: empty when no context applies).
 *  - Detail layer: route-GATED. On the results detail page (`/results/detail`)
 *    with a loaded `resultDetail`, append its serialized data (AC 1/2). On the
 *    Panel Trends page (`/results/trend`), append the decoded panel name. On
 *    any other page (or with nothing loaded) no detail is added — so navigating
 *    away naturally drops the previous page's detail (mobile clears the detail
 *    layer on navigation), and the context reflects the CURRENT page (AC 5).
 *  - Called fresh on every send (the components read the store live), so
 *    context is evaluated per message (AC 4).
 *  - Pure + framework-agnostic: takes a URL string and an already-read data
 *    snapshot; performs no data retrieval (AC 2).
 */
export function buildOrbiRequestContext(
  url: string | null | undefined,
  pageData?: OrbiPageDataSnapshot
): string[] {
  const context = matchOrbiRequestContext(url);

  const { path, query } = splitOrbiUrl(url);
  let detail: string | null = null;
  if (
    path.includes(ORBI_RESULTS_DETAIL_URL_FRAGMENT) &&
    pageData?.resultDetail
  ) {
    detail = serializeResultDetailContext(pageData.resultDetail);
  } else if (path.includes(ORBI_RESULTS_TREND_URL_FRAGMENT)) {
    detail = serializeTrendContext(query);
  }
  if (detail) {
    context.push(detail);
  }

  return context;
}
